import React, { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Building2, ChevronDown, Plus, Settings } from 'lucide-react';
import { useTeams } from '../../contexts/TeamsContext';
import { Team } from 'api-client';

export const TeamSwitcherMenu: React.FC = () => {
  const { teams, currentTeam, switchTeam, createTeam } = useTeams();
  const [isCreating, setIsCreating] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    
    setIsCreating(true);
    try {
      const newTeam = await createTeam(newTeamName.trim());
      setNewTeamName('');
      await switchTeam(newTeam);
      window.location.reload();
    } catch (err) {
      console.error('Error creating team:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleTeamSwitch = async (team: Team) => {
    try {
      await switchTeam(team);
      window.location.reload();
    } catch (err) {
      console.error('Error switching team:', err);
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="flex items-center space-x-2 px-3 py-1.5 rounded-lg hover:bg-[#1A1F2E] transition-colors group">
          <Building2 className="h-5 w-5 text-blue-500" />
          <span className="text-sm font-medium text-gray-300 group-hover:text-white">
            {currentTeam?.name || 'Select Team'}
          </span>
          <ChevronDown className="h-4 w-4 text-gray-500 group-hover:text-gray-400" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="w-64 bg-[#1A1F2E] rounded-lg border border-gray-800 shadow-xl animate-in fade-in-0 zoom-in-95"
          sideOffset={5}
          align="end"
        >
          <div className="px-1.5 py-2">
            {teams.map((team) => (
              <DropdownMenu.Item
                key={team.id}
                className="flex items-center px-2 py-2 rounded-md text-sm text-gray-300 hover:bg-gray-800/50 cursor-pointer outline-none"
                onSelect={() => handleTeamSwitch(team)}
              >
                <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                <div className="flex-1">
                  <div className="font-medium">{team.name}</div>
                  <div className="text-xs text-gray-500">
                    {team.isAdmin ? 'Admin' : 'Member'}
                  </div>
                </div>
                {team.id === currentTeam?.id && (
                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                )}
              </DropdownMenu.Item>
            ))}
          </div>

          <DropdownMenu.Separator className="h-px bg-gray-800 mx-1.5 my-1 hidden" />

          <div className="px-1.5 py-2 hidden">
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger className="flex items-center px-2 py-2 rounded-md text-sm text-gray-300 hover:bg-gray-800/50 cursor-pointer outline-none">
                <Plus className="h-4 w-4 text-gray-400 mr-2" />
                Create Team
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent
                  className="w-64 bg-[#1A1F2E] rounded-lg border border-gray-800 shadow-xl p-2"
                  sideOffset={2}
                  alignOffset={-5}
                >
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                      placeholder="Enter team name"
                      className="w-full px-2 py-1 bg-gray-800/50 border border-gray-700 rounded text-sm text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleCreateTeam}
                      disabled={isCreating || !newTeamName.trim()}
                      className="w-full px-2 py-1 bg-blue-500 text-white rounded text-sm font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCreating ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
            
            {currentTeam?.isAdmin && (
              <DropdownMenu.Item className="flex items-center px-2 py-2 rounded-md text-sm text-gray-300 hover:bg-gray-800/50 cursor-pointer outline-none">
                <Settings className="h-4 w-4 text-gray-400 mr-2" />
                Team Settings
              </DropdownMenu.Item>
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};